/**
 * Copyright 2022 (C) Blink - All Rights Reserved
 *
 * This source code is protected under international copyright law.  All rights
 * reserved and protected by the copyright holders.
 * All files are confidential and only available to authorized individuals with the
 * permission of the copyright holders. If you encounter any file and do not have
 * permission, please get in touch with the copyright holders and delete this file.
 */
import getFileExtension from "./getFileExtension";

const MIME_MAP = Object.freeze({
  // Images
  png: "image/png",
  jpeg: "image/jpeg",
  jpg: "image/jpeg",
  webp: "image/webp",
  gif: "image/gif",
  svg: "image/svg+xml",
  ico: "image/x-icon",
  avif: "image/avif",
  heic: "image/heic",
  heif: "image/heif",
  tiff: "image/tiff",
  bmp: "image/bmp",

  // Documents
  pdf: "application/pdf",
  xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  xls: "application/vnd.ms-excel",
  csv: "text/csv",
  json: "application/json",
  doc: "application/msword",
  docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  ppt: "application/vnd.ms-powerpoint",
  pptx: "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  txt: "text/plain",
  rtf: "application/rtf",
  xml: "application/xml",
  odt: "application/vnd.oasis.opendocument.text",
  ods: "application/vnd.oasis.opendocument.spreadsheet",

  // Videos
  mp4: "video/mp4",
  webm: "video/webm",
  mov: "video/quicktime",
  avi: "video/x-msvideo",
  mpeg: "video/mpeg",
  ogv: "video/ogg",
  flv: "video/x-flv",
  m4v: "video/x-m4v",

  // Audio
  mp3: "audio/mpeg",
  wav: "audio/wav",
  ogg: "audio/ogg",
  m4a: "audio/mp4",
  aac: "audio/aac",
  flac: "audio/flac",

  // Archives
  zip: "application/zip",
  rar: "application/vnd.rar",
  "7z": "application/x-7z-compressed",
  tar: "application/x-tar",
  gz: "application/gzip",
});

/**
 * Normalizes an extension (removes leading dot and converts to lowercase)
 */
const normalizeExtension = (ext) => {
  if (!ext) return "";
  return ext.startsWith(".") ? ext.slice(1).toLowerCase() : ext.toLowerCase();
};

/**
 * Returns the MIME type for a given extension or MIME type string.
 */
export const getExtensionMimeType = (input) => {
  if (!input) return "";
  const normalized = normalizeExtension(input);

  // Check if it's already a MIME type (exists as a value)
  const mimeFromValue = Object.values(MIME_MAP).find((m) => m === input);
  if (mimeFromValue) return mimeFromValue;

  return MIME_MAP[normalized] || "";
};

/**
 * Returns the extension for a given MIME type.
 * Returns the extension with a leading dot.
 */
export const mimeTypeToExtension = (mimeType) => {
  if (!mimeType) return "";
  const ext = Object.keys(MIME_MAP).find((m) => MIME_MAP[m] === mimeType);
  return ext ? `.${ext}` : "";
};

/**
 * Returns the MIME type for a given filename based on its extension.
 */
export const getFileMIME = (filename) => {
  if (!filename) return "";
  const ext = getFileExtension(filename);
  return MIME_MAP[normalizeExtension(ext)] || "";
};

export default getFileMIME;
